import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { SITE_URL, SEO_ROUTES, structuredData } from "./seo-config";

function getOrCreateMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }

  return element;
}

function getOrCreateCanonical() {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  return link;
}

function resolveSeoEntry(pathname) {
  return (
    SEO_ROUTES.find((entry) => (
      entry.path === "/"
        ? pathname === "/"
        : matchPath({ path: entry.path, end: true }, pathname)
    )) || { path: pathname, canonicalPath: pathname, title: "Page Not Found | Glastonbury Data", description: "This page could not be found.", noindex: true }
  );
}

export function SeoRouteMeta() {
  const location = useLocation();

  useEffect(() => {
    const { pathname } = location;
    const entry = resolveSeoEntry(pathname);
    const canonicalPath = entry.canonicalPath ?? pathname;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;

    document.title = entry.title;
    getOrCreateMeta('meta[name="robots"]', { name: "robots" })
      .setAttribute("content", entry.noindex ? "noindex, follow" : "index, follow, max-image-preview:large");
    let schema = document.getElementById("site-structured-data");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "site-structured-data";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(structuredData(entry, canonicalUrl));

    const descriptionMeta = getOrCreateMeta('meta[name="description"]', {
      name: "description",
    });
    descriptionMeta.setAttribute("content", entry.description);

    const ogTitle = getOrCreateMeta('meta[property="og:title"]', {
      property: "og:title",
    });
    ogTitle.setAttribute("content", entry.title);

    const ogDescription = getOrCreateMeta('meta[property="og:description"]', {
      property: "og:description",
    });
    ogDescription.setAttribute("content", entry.description);

    const ogType = getOrCreateMeta('meta[property="og:type"]', {
      property: "og:type",
    });
    ogType.setAttribute("content", "website");

    const ogUrl = getOrCreateMeta('meta[property="og:url"]', {
      property: "og:url",
    });
    ogUrl.setAttribute("content", canonicalUrl);

    const twitterCard = getOrCreateMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
    });
    twitterCard.setAttribute("content", "summary");

    const twitterTitle = getOrCreateMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
    });
    twitterTitle.setAttribute("content", entry.title);

    const twitterDescription = getOrCreateMeta(
      'meta[name="twitter:description"]',
      {
        name: "twitter:description",
      },
    );
    twitterDescription.setAttribute("content", entry.description);

    const canonicalLink = getOrCreateCanonical();
    canonicalLink.setAttribute("href", canonicalUrl);
  }, [location]);

  return null;
}
